// ShareBar.jsx
// A share button for public pages: uses the native share sheet where
// available (mobile), falling back to copying the link to the clipboard.

// Packages, Dependencies, and Shared Libraries
import React, { useState } from "react";

/*******************************************************************************
 * @func ShareBar (React Component)
 * A pill-shaped share button. On tap, opens the native share sheet with the
 * given title/text/url (WhatsApp, iMessage, etc. render our rich preview),
 * or copies the link and confirms when the browser has no share sheet.
 *
 * @param title (string): Share sheet title
 * @param text (string): Share sheet message text
 * @param url (string, optional): Absolute URL to share; defaults to the
 * current page
 * @param label (string, optional): Button label, defaults to "Share"
 * @return JSX Element
 *
 * @notes User cancelling the native sheet is not an error
 *
 ******************************************************************************/
const ShareBar = ({ title, text, url, label = "Share" }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || window.location.href;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title, text, url: shareUrl }).catch(() => {});
      return;
    }
    const confirmCopied = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).then(confirmCopied, () => {});
    } else {
      // Ancient-browser fallback
      window.prompt("Copy this link:", shareUrl);
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="inline-flex items-center gap-2 bg-white text-leaf-green-dark font-bold text-sm rounded-full px-5 py-2 shadow-card border border-leaf-green/25 hover:bg-leaf-green-soft transition-colors font-global"
    >
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z"
        />
      </svg>
      {copied ? "Link copied!" : label}
    </button>
  );
}; // End ShareBar

export default ShareBar;
